import React from "react";
import {useForm, Controller} from "react-hook-form";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import {
  useNavigate,
  Link,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import {singleSelectDropdownStyles} from "components/ReportComponents/singleSelectDropdownStyles";
import {FormButton} from "components/FormComponents/Button/FormButton";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import ThemeProvider from "@mui/material/styles/ThemeProvider";
import {
  initialMessage,
  initialMessages,
  isValidObject,
  loggedMessage,
  message,
  messages,
  scrollToTop,
  theme,
} from "commonUtils/utils";
import {MenuItem} from "@mui/material";
import {DatePicker, LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import {activityStyles} from "../activityStyles";
import {CPSConstants, PageNames} from "commonUtils/enumerations";
import {store} from "../../../../redux/store";
import {getRequest, setHeader} from "repository/genericRepository";
import {ActivityDescriptions, handleAxiosErrors} from "../activityUtils";
import {ARF_BASIC_SEARCH_CONTROL} from "commonUtils/globalAPISettings";

const SearchARF = () => {
  const navigate = useNavigate();
  const location = useLocation();
  let [searchParams] = useSearchParams();

  const [loader, setLoader] = React.useState<boolean>(false);
  const [servervalidation, setServerValidation] =
    React.useState<message>(initialMessage);
  const [serverMessagesList, setServerMessagesList] =
    React.useState<messages[]>(initialMessages);
  const [showAlert, setShowAlert] = React.useState<boolean>(false);
  const [errorMsg, setErrorMsg] = React.useState<string>("");
  const [arfActivityDescriptionsList, setARFActivityDescriptionsList] =
    React.useState<ActivityDescriptions[]>([]);

  const PLEASE_SELECT = -1;
  const defaultFromValues = {
    arfId: "",
    tlMaPCAId: "DFCA-",
    startDate: null,
    endDate: null,
    activityDescriptionId: PLEASE_SELECT,
    cmsAssociate: "",
    advisingCentralContract: "",
  };

  const {handleSubmit, control, setValue, getValues} = useForm<any>({
    defaultValues: defaultFromValues,
  });

  React.useEffect(() => {
    scrollToTop(0, "smooth");
    fetchARFBasicSearchControls();
  }, []);

  const fetchARFBasicSearchControls = React.useMemo(
    () => async () => {
      try {
        setLoader(true);
        const authState = store.getState();

        const APIHeaders = {
          Authorization: `Bearer ${authState.auth?.authDetails?.token}`,
        };

        const responseData = await getRequest({
          url: ARF_BASIC_SEARCH_CONTROL,
          headers: setHeader(APIHeaders),
        });
        setLoader(false);
        if (isValidObject(responseData)) {
          setARFActivityDescriptionsList(responseData?.activityDescriptions);
        }
      } catch (error: any) {
        handleAxiosErrors(
          error,
          setErrorMsg,
          setShowAlert,
          setServerValidation,
        );
        loggedMessage(error, errorMsg, PageNames.ACTIVITY);
        setLoader(false);
      }
    },
    [],
  );

  const handleOnDateBlur = (key: string) => {
    let value = getValues(`${key}`);

    if (value?.$d && !value?.isValid()) {
      setValue(key, null);
    }
  };

  const searchARFResult = (data: any) => {
    navigate("/activity/searcharfresult", {
      state: {...data, isBasicSearch: true},
    });
  };

  return (
    <>
      <Box
        component="form"
        onSubmit={(event: React.FormEvent<HTMLFormElement>) => {
          event.preventDefault();
          handleSubmit(searchARFResult)(event);
        }}
        noValidate
        sx={{
          ...activityStyles.activitySearchContainer,
          ...activityStyles.activityBoxStyle,
        }}>
        <Box sx={activityStyles.activityBasicBoxContainer}>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <InputLabel sx={singleSelectDropdownStyles.muilabel}>
                ARF #
              </InputLabel>
              <Controller
                name="arfId"
                control={control}
                render={({field}) => (
                  <TextField
                    type="text"
                    size="small"
                    className="input-font-family"
                    fullWidth
                    {...field}
                    sx={activityStyles.activityTextField}
                    inputProps={{
                      maxLength: CPSConstants.MAX_LENGTH_FIFTY,
                    }}></TextField>
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <InputLabel sx={singleSelectDropdownStyles.muilabel}>
                Dragonfly DFCA #
              </InputLabel>
              <Controller
                name="tlMaPCAId"
                control={control}
                render={({field}) => (
                  <TextField
                    type="text"
                    size="small"
                    className="input-font-family"
                    fullWidth
                    {...field}
                    sx={activityStyles.activityTextField}
                    inputProps={{
                      maxLength: CPSConstants.MAX_LENGTH_FIFTY,
                    }}></TextField>
                )}
              />
            </Grid>
            <Grid
              item
              xs={12}
              className="custom-datepicker"
              sx={{
                ...activityStyles.datePckerPaddingIcon,
              }}>
              <InputLabel sx={singleSelectDropdownStyles.muilabel}>
                Start Date
              </InputLabel>
              <Controller
                name={`startDate`}
                control={control}
                render={({field}) => (
                  <ThemeProvider theme={theme}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        {...field}
                        onChange={(newValue: any) => {
                          field.onChange(newValue);
                        }}
                        renderInput={(params: any) => (
                          <TextField
                            className="input-font-family"
                            size={"small"}
                            sx={activityStyles.activityTextField}
                            {...params}
                            onBlur={e => handleOnDateBlur("startDate")}
                          />
                        )}
                      />
                    </LocalizationProvider>
                  </ThemeProvider>
                )}
              />
            </Grid>
            <Grid
              item
              xs={12}
              className="custom-datepicker"
              sx={{
                ...activityStyles.datePckerPaddingIcon,
              }}>
              <InputLabel sx={singleSelectDropdownStyles.muilabel}>
                End Date
              </InputLabel>
              <Controller
                name={`endDate`}
                control={control}
                render={({field}) => (
                  <ThemeProvider theme={theme}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        {...field}
                        onChange={(newValue: any) => {
                          field.onChange(newValue);
                        }}
                        renderInput={(params: any) => (
                          <TextField
                            className="input-font-family"
                            size={"small"}
                            sx={activityStyles.activityTextField}
                            {...params}
                            onBlur={e => handleOnDateBlur("endDate")}
                          />
                        )}
                      />
                    </LocalizationProvider>
                  </ThemeProvider>
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <InputLabel sx={singleSelectDropdownStyles.muilabel}>
                Activity Description
              </InputLabel>
              <Controller
                name="activityDescriptionId"
                control={control}
                render={({field}) => (
                  <ThemeProvider theme={theme}>
                    <Select
                      size="small"
                      className="search-basic-select-box"
                      {...field}
                      sx={activityStyles.activityTextField}>
                      {arfActivityDescriptionsList?.map(
                        (item: ActivityDescriptions, index: number) => (
                          <MenuItem
                            key={index}
                            value={item.id}
                            title={item.name}>
                            {item.name}
                          </MenuItem>
                        ),
                      )}
                    </Select>
                  </ThemeProvider>
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <InputLabel sx={singleSelectDropdownStyles.muilabel}>
                CMS Associate
              </InputLabel>
              <Controller
                name="cmsAssociate"
                control={control}
                render={({field}) => (
                  <TextField
                    type="text"
                    size="small"
                    className="input-font-family"
                    fullWidth
                    {...field}
                    sx={activityStyles.activityTextField}
                    inputProps={{
                      maxLength: CPSConstants.MAX_LENGTH_FIFTY,
                    }}></TextField>
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <InputLabel sx={singleSelectDropdownStyles.muilabel}>
                Advising Central Contact
              </InputLabel>
              <Controller
                name="advisingCentralContract"
                control={control}
                render={({field}) => (
                  <TextField
                    type="text"
                    size="small"
                    className="input-font-family"
                    fullWidth
                    {...field}
                    sx={activityStyles.activityTextField}
                    inputProps={{
                      maxLength: CPSConstants.MAX_LENGTH_FIFTY,
                    }}></TextField>
                )}
              />
            </Grid>
            <Grid item xs={12} mt={1}>
              <FormButton
                variant="contained"
                size="small"
                type="submit"
                outlet="Search Activities"
              />
              <Link to="/activity/advancesearcharf">
                <Box sx={activityStyles.advanceSearchLink}>
                  <KeyboardDoubleArrowRightIcon />
                  Advanced Search
                </Box>
              </Link>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </>
  );
};

export default SearchARF;
